import { TutorialTooltipStep } from "../../components/TutorialTooltip";

/**
 * Tutorial configuration for the help tooltip tutorial.
 * Each step corresponds to a Figma design in the flow.
 * 
 * Steps 1-2: Step 1 is modal on dashboard, Step 2 is tooltip on dashboard
 * Step 3: Tooltip on PraxisPlay HCP selection page
 * Steps 4-6: Tooltips during active practice session
 * Steps 7-11: Tooltips on Insights/Report page
 * Step 12: Final tooltip on dashboard ("Got it")
 */
import { ensurePraxisPlayNavVisible } from "./utils";

export const tutorialSteps: TutorialTooltipStep[] = [
  {
    id: "step-1",
    anchorSelector: "body",
    isModal: true,
    route: "/dashboard",
    title: "Practice real HCP conversations. Get better fast.",
    description: "PraxisPro lets you role-play realistic HCP conversations, get AI feedback, and track your improvement over time. This quick tour will show you exactly how to start and what to focus on.",
    actionButtonText: "Start Tour",
    skipButtonText: "Skip for now",
    onAction: ensurePraxisPlayNavVisible,
  },
  {
    id: "step-2",
    anchorSelector: "[data-tutorial-praxisplay-nav]",
    position: "right-start",
    route: "/dashboard",
    title: "Start with PraxisPlay",
    description: "PraxisPlay is where you practice live conversations with AI HCPs. This is the fastest way to improve your score and skills.",
    actionButtonText: "Start PraxisPlay",
    skipButtonText: "Skip for now",
    onAction: async () => {
      // Click the PraxisPlay nav item to navigate
      const praxisplayNav = document.querySelector("[data-tutorial-praxisplay-nav]");
      if (praxisplayNav) {
        (praxisplayNav as HTMLElement).click();
        // Wait for navigation
        await new Promise(resolve => setTimeout(resolve, 800));
      } else {
        // Fallback: navigate directly
        window.location.href = "/praxisplay/role-play";
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    },
  },
  {
    id: "step-3",
    anchorSelector: "[data-tutorial-hcp-card-selected]",
    position: "right",
    route: "/praxisplay/role-play",
    title: "Choose Your HCP",
    description: "Each HCP has a unique specialty and personality. Start with one that matches who you see in the field. You can always customize or try other AI HCPs later.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
    onAction: async () => {
      // Ensure practice mode is enabled (handled by PraxisPlay component useEffect)
      // Click the "Begin PraxisPlay" button to start the session
      const buttons = Array.from(document.querySelectorAll('button'));
      const beginBtn = buttons.find(btn => btn.textContent?.includes('Begin PraxisPlay'));
      if (beginBtn) {
        (beginBtn as HTMLElement).click();
        // Wait for session to start
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    },
  },
  {
    id: "step-4",
    anchorSelector: "[data-tutorial-microphone-button]",
    position: "right-end",
    requiresActiveSession: true,
    title: "Talk Like You Would in Real Life",
    description: "Speak naturally. The AI responds in real-time and adapts to how you communicate. Use the mute button to avoid picking up unwanted noise around you.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
    // Don't click the button - just move to next step
    onAction: async () => {
      // No action needed - just progress to next step
    },
  },
  {
    id: "step-5",
    anchorSelector: "[data-tutorial-end-call-button]",
    position: "left-end",
    requiresActiveSession: true,
    title: "End When You're Ready",
    description: "Sessions are typically 3-7 minutes. End when you've delivered your key message or handled objections.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
    // Don't click the button - just move to next step
    onAction: async () => {
      // No action needed - just progress to next step
    },
  },
  {
    id: "step-6",
    anchorSelector: "[data-tutorial-sales-aid-panel]",
    position: "bottom-end",
    requiresActiveSession: true,
    title: "Refer to the Sales Aid Panel",
    description: "This is where you can view and reference your CVA or any relevant document during your call.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
  },
  {
    id: "step-7",
    anchorSelector: "[data-tutorial-performance-tab]",
    position: "bottom",
    showWhenElementOnPage: true,
    title: "Performance Tab",
    description: "The Performance Tab gives you a breakdown of your overall performance.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
  },
  {
    id: "step-8",
    anchorSelector: "#praxis-score",
    position: "bottom",
    showWhenElementOnPage: true,
    title: "Your Praxis Score",
    description: "This score reflects how effectively you communicated overall. Focus on improvement over time, not perfection.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
  },
  {
    id: "step-9",
    anchorSelector: "#call-insights",
    position: "top-end", // tooltip right-aligned above anchor; arrow appears on left of tooltip
    showWhenElementOnPage: true,
    title: "View your Core Competency Insights",
    description: "These insights highlight your strengths and growth areas. Lower scores show where your next practice session will have the biggest impact.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
  },
  {
    id: "step-10",
    anchorSelector: "#improvement-areas",
    position: "left-start",
    showWhenElementOnPage: true,
    title: "Improvement Areas",
    description: "These are targeted recommendations based on specific moments from your conversation. Each insight explains what happened, why it mattered, and how to improve in future sessions.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
  },
  {
    id: "step-11",
    anchorSelector: "#session-recap-tab",
    position: "bottom",
    showWhenElementOnPage: true,
    title: "Session Recap Tab",
    description: "The Session Recap Tab shows you an in-depth breakdown of each of your Core Competency Insights and how to improve in each area.",
    actionButtonText: "Next",
    skipButtonText: "Skip for now",
  },
  {
    id: "step-12",
    anchorSelector: "[data-tutorial-dashboard-nav]",
    position: "right",
    showWhenElementOnPage: true,
    title: "Track Your Progress Here",
    description: "This dashboard shows your scores, practice history, and skill trends over time. The more you practice, the more this evolves.",
    actionButtonText: "Got it",
    skipButtonText: "Skip for now",
    isLastStep: true,
  },
];
